import { useEffect, useState } from 'react';
import axios from 'axios';
import LockdownPage from './LockdownPage';

/**
 * LockdownInterceptor — global axios + fetch interceptor that flips the
 * UI into the LockdownPage as soon as the backend returns a 503 with
 * `X-Lockdown: 1` (or a JSON body containing `lockdown: true`).
 *
 * Once locked, polls `/api/maintenance-status` every 12s; the page
 * auto-recovers when the backend clears the lockdown.
 *
 * Mount once at the app root, just below the providers and above
 * the rest of the tree:
 *
 *   <BrowserRouter>
 *     <LanguageProvider>
 *       <LockdownInterceptor>
 *         <App routes...>
 *       </LockdownInterceptor>
 *     </LanguageProvider>
 *   </BrowserRouter>
 */
const POLL_MS = 12_000;

const detectLockdown = (err) => {
  if (!err) return false;
  if (err.response?.status === 503) {
    if (err.response.headers?.['x-lockdown'] === '1') return true;
    if (err.response.data && err.response.data.lockdown === true) return true;
  }
  return false;
};

const isAutoTriggered = (responseOrErr) => {
  // Header the backend lockdown middleware sets when state.auto_triggered=true
  const h = responseOrErr?.response?.headers || responseOrErr?.headers;
  if (h && (h['x-lockdown-auto'] === '1' || h.get?.('x-lockdown-auto') === '1')) return true;
  const body = responseOrErr?.response?.data;
  if (body && body.auto_triggered === true) return true;
  return false;
};

const LockdownInterceptor = ({ children }) => {
  const [locked, setLocked] = useState(false);
  const [autoTriggered, setAutoTriggered] = useState(false);

  useEffect(() => {
    // axios global response interceptor — picks up every API failure
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (detectLockdown(err)) {
          setLocked(true);
          if (isAutoTriggered(err)) setAutoTriggered(true);
        }
        return Promise.reject(err);
      },
    );

    // fetch interceptor (for components using window.fetch)
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const res = await origFetch(...args);
        if (res.status === 503 && res.headers.get('x-lockdown') === '1') {
          setLocked(true);
          if (res.headers.get('x-lockdown-auto') === '1') setAutoTriggered(true);
        }
        return res;
      } catch (e) {
        throw e;
      }
    };

    return () => {
      axios.interceptors.response.eject(id);
      window.fetch = origFetch;
    };
  }, []);

  // Poll for recovery — also picks up auto-trigger flag from /maintenance-status
  useEffect(() => {
    let stopped = false;
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const probe = async () => {
      try {
        const res = await fetch(`${apiBase}/api/maintenance-status`, { cache: 'no-store' });
        if (stopped) return;
        if (res.status === 503 && res.headers.get('x-lockdown') === '1') {
          setLocked(true);
          if (res.headers.get('x-lockdown-auto') === '1') setAutoTriggered(true);
        } else if (locked) {
          setLocked(false);
          setAutoTriggered(false);
        }
      } catch { /* keep trying */ }
    };
    // Probe immediately on mount AND every POLL_MS while locked
    probe();
    const id = setInterval(probe, locked ? POLL_MS : 30_000);
    return () => { stopped = true; clearInterval(id); };
  }, [locked]);

  if (locked) {
    return (
      <LockdownPage
        autoTriggered={autoTriggered}
        onCheck={() => window.location.reload()}
      />
    );
  }
  return children;
};

export default LockdownInterceptor;
